/**
 * RestoID — the "set a new password" fields.
 * ---------------------------------------------------------------------------
 * A RestoID-shaped stand-in for the upstream `@/containers/SetPassword` tree
 * (`src/containers/SetPassword/index.tsx` → `Lite.tsx` / `SetPassword.tsx`).
 *
 * Upstream renders 44px `PasswordInputField`s — a floating label notched into a
 * fieldset border, an upstream eye/clear suffix — plus one shared "show password"
 * checkbox under the pair. That is a structurally different control from RestoID's
 * `Field` (label ABOVE, own fill, radius 13, lime focus ring, `SIZE.field` tall,
 * per-field eye toggle), and the theme overlay can only repaint colors, never
 * restructure. So the PRESENTATION is rebuilt here while the FORM LOGIC stays
 * equivalent to upstream, line for line:
 *
 *   - the same `useForgotPasswordSettings()` split — forgot-password ENABLED gives
 *     the single-field "Lite" form, DISABLED gives password + confirm-password
 *     (counter-intuitive, but that is exactly what upstream's index.tsx does);
 *   - the same react-hook-form setup (`reValidateMode: 'onBlur'`, same defaults) and
 *     the same rules, so the same messages: `error.password_required` on an empty
 *     password, `error.passwords_do_not_match` on a confirm mismatch. The password
 *     POLICY itself is checked by the page's `usePasswordPolicyChecker` on submit and
 *     arrives back here as the `errorMessage` prop, exactly as before;
 *   - the same `clearErrorMessage()` on every submit and whenever the form turns
 *     invalid, and the same `errorMessage` block under the fields;
 *   - the same upstream `HiddenIdentifierInput`, imported as-is, so password managers
 *     still pair the new password with the right username;
 *   - `autoComplete="new-password"` on every field, `autoFocus` on the first one,
 *     `maxLength` from the policy, and the submit button loading off `isSubmitting`.
 *
 * Three deliberate differences, all forced by the control swap:
 *   - the shared "show password" checkbox (`action.show_password`) is gone — every
 *     `Field password` carries RestoID's own eye toggle instead;
 *   - the per-field clear button (upstream's `ClearIcon` suffix, standard form only)
 *     goes with it; RestoID fields have no clear affordance;
 *   - `Field` couples the red border to the error text, so the confirm field gets
 *     `danger` as well. Upstream passed `errorMessage` there but forgot `isDanger`,
 *     which on a RestoID field would have swallowed the mismatch message entirely.
 *
 * And one fix: `maxLength` actually reaches the inputs. Upstream declares the prop in
 * `index.tsx`, spreads it into both forms and then never reads it, so the policy's
 * ceiling — which the page computes and passes — did nothing at all.
 */
import { useCallback, useEffect, type ReactNode } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import HiddenIdentifierInput from '@/containers/SetPassword/HiddenIdentifierInput';
import { useForgotPasswordSettings } from '@/hooks/use-sie';

import { Button, C, FONT, Field, Stack } from './restoid';

/** Mirrors the upstream container's props 1:1 so the swap stays drop-in. */
type Props = {
  className?: string;
  autoFocus?: boolean;
  onSubmit: (password: string) => Promise<void>;
  errorMessage?: string;
  clearErrorMessage?: () => void;
  maxLength?: number;
  /** Whether the password is being set in a reset password flow. */
  isForgotPassword?: boolean;
};

/**
 * The form-level error (policy rejection, "same password", API failure) — upstream's
 * `<ErrorMessage>` under the fields, in RestoID's error type. `role="alert"` is kept
 * so a screen reader still announces it the moment the submit comes back.
 */
const FormError = ({ children }: { children: ReactNode }) => (
  <div role="alert" style={{ color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}>
    {children}
  </div>
);

/** Single-field form — used when forgot-password is ENABLED (upstream `Lite.tsx`). */
const LiteForm = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  maxLength,
  isForgotPassword,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<{ newPassword: string }>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '' },
  });

  const { field, fieldState } = useController({
    control,
    name: 'newPassword',
    rules: { required: t('error.password_required') },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  return (
    <form
      className={className}
      onSubmit={(event) => {
        void onSubmitHandler(event);
      }}
    >
      {/* Outside the Stack: it is visually hidden, and a hidden child would still
          take a slot in the stagger animation. */}
      <HiddenIdentifierInput isForgotPassword={isForgotPassword} />
      <Stack gap={18}>
        <Field
          password
          label={t('input.password')}
          autoComplete="new-password"
          autoFocus={autoFocus}
          maxLength={maxLength}
          name={field.name}
          value={field.value}
          danger={Boolean(fieldState.error)}
          errorMessage={fieldState.error?.message}
          inputRef={field.ref}
          onChange={(next) => field.onChange(next)}
          onBlur={field.onBlur}
        />
        {errorMessage ? <FormError>{errorMessage}</FormError> : null}
        <Button htmlType="submit" title={t('action.save_password')} isLoading={isSubmitting} />
      </Stack>
      <input hidden type="submit" />
    </form>
  );
};

/**
 * Password + confirm-password form — used when forgot-password is DISABLED
 * (upstream `SetPassword.tsx`).
 */
const StandardForm = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  maxLength,
  isForgotPassword,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<{ newPassword: string; confirmPassword: string }>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const password = useController({
    control,
    name: 'newPassword',
    rules: { required: t('error.password_required') },
  });

  const confirmPassword = useController({
    control,
    name: 'confirmPassword',
    // Upstream's rule, verbatim: compare against the live `newPassword` value.
    rules: {
      validate: (value) => value === watch('newPassword') || t('error.passwords_do_not_match'),
    },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  return (
    <form
      className={className}
      onSubmit={(event) => {
        void onSubmitHandler(event);
      }}
    >
      <HiddenIdentifierInput isForgotPassword={isForgotPassword} />
      <Stack gap={18}>
        <Field
          password
          label={t('input.password')}
          autoComplete="new-password"
          autoFocus={autoFocus}
          maxLength={maxLength}
          name={password.field.name}
          value={password.field.value}
          danger={Boolean(password.fieldState.error)}
          errorMessage={password.fieldState.error?.message}
          inputRef={password.field.ref}
          onChange={(next) => password.field.onChange(next)}
          onBlur={password.field.onBlur}
        />
        <Field
          password
          label={t('input.confirm_password')}
          autoComplete="new-password"
          maxLength={maxLength}
          name={confirmPassword.field.name}
          value={confirmPassword.field.value}
          danger={Boolean(confirmPassword.fieldState.error)}
          errorMessage={confirmPassword.fieldState.error?.message}
          inputRef={confirmPassword.field.ref}
          onChange={(next) => confirmPassword.field.onChange(next)}
          onBlur={confirmPassword.field.onBlur}
        />
        {errorMessage ? <FormError>{errorMessage}</FormError> : null}
        <Button htmlType="submit" title={t('action.save_password')} isLoading={isSubmitting} />
      </Stack>
      <input hidden type="submit" />
    </form>
  );
};

/**
 * Picks the form the same way `src/containers/SetPassword/index.tsx` does: the
 * one-field Lite form when the forgot-password flow is enabled, the
 * password + confirm-password form when it is not.
 */
const SetPasswordFields = (props: Props) => {
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();

  return isForgotPasswordEnabled ? <LiteForm {...props} /> : <StandardForm {...props} />;
};

export default SetPasswordFields;
